import {
	AcquisitionEvaluationStageMap,
	AcquisitionTransactionTypeMap,
	DealRequestAllocation,
	DealTypeMap,
	NatureOfBuyerTypeMap,
} from '../../shared/models';
import { ReactComponent as NewBusiness } from '../../shared/icons/NewBusiness.svg';
import { DebtCapitalHeading } from './DebtCapitalHeading';
import {
	CerebroComplexListItem,
	CerebroHeading,
	CerebroLabel,
	CerebroStyledBox,
	CerebroText,
} from '../../shared/components';
import { Grid } from '@mui/material';
import { NumberUtils } from '../../shared/utils';

interface DebtAcquiringBusinessProps {
	allocation: DealRequestAllocation;
}

export const DebtAcquiringBusiness: React.FC<DebtAcquiringBusinessProps> = ({ allocation }) => {
	const isEmpty = isDebtAcquiringBusinessEmpty(allocation);

	return (
		<>
			<DebtCapitalHeading icon={NewBusiness} amountRequested={allocation.totalAcquisitionCapitalNeededAmount}>
				Acquiring Business
			</DebtCapitalHeading>

			{isEmpty ? (
				<CerebroStyledBox sx={{ marginBottom: 8 }}>
					<CerebroHeading size="h2" component="h2">
						No details have been added yet.
					</CerebroHeading>
					<br />
				</CerebroStyledBox>
			) : (
				<CerebroComplexListItem sx={{ marginTop: 2, marginBottom: 8 }}>
					<Grid container>
						{allocation.dealType !== undefined ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									What type of acquisition are you doing?
								</CerebroLabel>
								<CerebroText variant="small" component="span">
									{DealTypeMap[allocation.dealType]}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.acquisitionEvaluationStage !== undefined ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									What stage are you in evaluating the acquisition target?
								</CerebroLabel>
								<CerebroText variant="small" component="span">
									{AcquisitionEvaluationStageMap[allocation.acquisitionEvaluationStage]}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.acquisitionYearsInOperation !== undefined ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									How many years has the acquisition target been operating?
								</CerebroLabel>
								<CerebroText variant="small" component="span">
									{allocation.acquisitionYearsInOperation}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.transactionType !== undefined ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									What is the transaction type?
								</CerebroLabel>
								<CerebroText variant="small" component="span">
									{AcquisitionTransactionTypeMap[allocation.transactionType]}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.natureOfBuyer !== undefined ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									What is the nature of the buyer?
								</CerebroLabel>
								<CerebroText variant="small" component="span">
									{NatureOfBuyerTypeMap[allocation.natureOfBuyer]}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}

						{allocation.hasLegalCouncel !== undefined && allocation.hasLegalCouncel ? (
							<Grid item xs={6}>
								<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>
									Name of company&apos;s legal firm
								</CerebroLabel>
								<CerebroText variant="small" component="span">
									{allocation.legalFirmName}
								</CerebroText>
							</Grid>
						) : (
							<></>
						)}
						<Grid item xs={12}>
							<CerebroLabel sx={{ marginTop: 1.5, marginBottom: 1.5 }}>Acquisition Totals</CerebroLabel>
							<AcquisitionFormulaDisplay allocation={allocation} />
						</Grid>
					</Grid>
				</CerebroComplexListItem>
			)}
		</>
	);
};

export function isDebtAcquiringBusinessEmpty(allocation: DealRequestAllocation): boolean {
	return allocation.acquiringBusiness == undefined || !allocation.acquiringBusiness;
}

interface AcquisitionFormulaDisplayProps {
	allocation: DealRequestAllocation;
}

const AcquisitionFormulaDisplay = ({ allocation }: AcquisitionFormulaDisplayProps) => {
	const hasWorkingCapitalLine = allocation.workingCapitalForAcquisition ?? false;
	const formattedOfferedAmount = NumberUtils.FormatNumber(allocation.valueOfferedAmount ?? 0, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedCashProvided = NumberUtils.FormatNumber(allocation.buyerCashAmount ?? 0, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedTransactionCosts = NumberUtils.FormatNumber(
		allocation.acquisitionTransactionCostsAmount ?? 0,
		'currency',
		{
			maximumFractionDigits: 0,
		}
	);

	const formattedWorkingCapitalContribution = NumberUtils.FormatNumber(
		allocation.acquisitionWorkingCapitalContributionAmount ?? 0,
		'currency',
		{
			maximumFractionDigits: 0,
		}
	);

	const formattedEquityRollProvided = NumberUtils.FormatNumber(allocation.sellerEquityRollAmount ?? 0, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedSellerProvided = NumberUtils.FormatNumber(allocation.sellerFinancingAmount ?? 0, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedEarnoutAmount = NumberUtils.FormatNumber(allocation.earnoutAmount ?? 0, 'currency', {
		maximumFractionDigits: 0,
	});

	const formattedWorkingCapitalLineAmount = NumberUtils.FormatNumber(
		allocation.workingCapitalForAcquisitionAmount ?? 0,
		'currency',
		{
			maximumFractionDigits: 0,
		}
	);

	let cashProvidedFormula = `${formattedCashProvided} (Cash)`;
	if ((allocation.acquisitionTransactionCostsAmount ?? 0) > 0) {
		cashProvidedFormula += ` + ${formattedTransactionCosts} (Transaction Costs)`;
	}
	if ((allocation.acquisitionWorkingCapitalContributionAmount ?? 0) > 0) {
		cashProvidedFormula += ` + ${formattedWorkingCapitalContribution} (Working Capital)`;
	}

	const totalAcquisitionAmount = NumberUtils.FormatNumber(
		allocation.totalAcquisitionCapitalNeededAmount,
		'currency',
		{
			maximumFractionDigits: 0,
		}
	);

	const formula = hasWorkingCapitalLine
		? `${formattedOfferedAmount} (Offered Value) - ${formattedEquityRollProvided} (Equity Roll) - ${formattedEarnoutAmount} (Earnout) - ${cashProvidedFormula} - ${formattedWorkingCapitalLineAmount} (From Working Capital Line) - ${formattedSellerProvided} (Seller Financing) = ${totalAcquisitionAmount} (Total Request Amount)`
		: `${formattedOfferedAmount} (Offered Value) - ${formattedEquityRollProvided} (Equity Roll) - ${formattedEarnoutAmount} (Earnout) - ${cashProvidedFormula} - ${formattedSellerProvided} (Seller Financing) = ${totalAcquisitionAmount} (Total Request Amount)`;

	return <CerebroText variant="small">{formula}</CerebroText>;
};
